import {
  adminProfileSchema,
  adminSessionSchema,
  canMutate,
  canRevealHealth,
  type AdminProfile,
} from '@steado/admin-contract/auth';
import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { z } from 'zod';

import { API_BASE_URL } from '@/lib/api-base-url';
import { ApiError } from '@/lib/api/api-error';
import { createApiClient, type ApiClient, type Credentials } from '@/lib/api/client';

import { clearSession, readSession, writeSession, type SessionStorage, type StoredSession } from './session';

/** Only the fields the console needs; the consumer session carries more. */
const refreshResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
});

export type AuthStatus = 'restoring' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly admin: AdminProfile | null;
  /** Use for every call except sign-in; it refreshes and retries on a 401. */
  readonly api: ApiClient;
  readonly signIn: (email: string, password: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  /** Mirrors the server-side role checks, so a read-only operator is not shown buttons that will 403. */
  readonly can: { readonly mutate: boolean; readonly revealHealth: boolean };
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  readonly children: ReactNode;
  readonly storage?: SessionStorage;
  readonly fetchImpl?: typeof fetch;
  /**
   * Starts signed in, skipping the restore-and-revalidate step. For tests that
   * are about something other than sign-in; the restore path has its own.
   */
  readonly initialSession?: StoredSession;
}

const NO_STORAGE: SessionStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

function defaultStorage(): SessionStorage {
  return typeof globalThis.sessionStorage === 'undefined' ? NO_STORAGE : globalThis.sessionStorage;
}

export function AuthProvider({ children, storage, fetchImpl, initialSession }: AuthProviderProps) {
  const store = useMemo(() => storage ?? defaultStorage(), [storage]);

  const [session, setSession] = useState<StoredSession | null>(() => initialSession ?? readSession(store));

  const [isRestoring, setRestoring] = useState(
    () => initialSession === undefined && readSession(store) !== null,
  );

  const applySession = useCallback(
    (next: StoredSession | null) => {
      setSession(next);
      if (next === null) {
        clearSession(store);
      } else {
        writeSession(store, next);
      }
    },
    [store],
  );

  /**
   * Folds a rotation into the existing session. The functional form reads the
   * current session without a ref, which matters because this is called from
   * inside the API client rather than from a render.
   */
  const applyCredentials = useCallback(
    (credentials: Credentials | null) => {
      setSession((current) => {
        if (credentials === null || current === null) {
          clearSession(store);
          return null;
        }
        const next = { ...current, ...credentials };
        writeSession(store, next);
        return next;
      });
    },
    [store],
  );

  /**
   * A plain client for the auth endpoints themselves — none of them take a
   * bearer token, and giving this one the refresh hook would let a failing
   * refresh call refresh, recursively.
   */
  const auth = useMemo(
    () =>
      createApiClient({
        baseUrl: API_BASE_URL,
        ...(fetchImpl === undefined ? {} : { fetchImpl }),
      }),
    [fetchImpl],
  );

  const api = useMemo(
    () =>
      createApiClient({
        baseUrl: API_BASE_URL,
        onRefresh: async (refreshToken) =>
          auth.request({
            path: '/auth/refresh',
            method: 'POST',
            body: { refreshToken },
            schema: refreshResponseSchema,
            skipReauth: true,
          }),
        // A rotation happens inside the client, so the persisted session has to
        // be caught up here or a reload would restore a spent refresh token.
        onCredentialsChanged: (credentials) => {
          applyCredentials(credentials);
        },
        ...(fetchImpl === undefined ? {} : { fetchImpl }),
      }),
    [auth, fetchImpl, applyCredentials],
  );

  /**
   * Keeps the client's credentials in step with the session.
   *
   * Declared before the restore effect so the token is in place before that
   * effect issues its first request. Pushing them in rather than having the
   * client pull them back out of React is what keeps refs out of this file.
   */
  useEffect(() => {
    api.setCredentials(
      session === null ? null : { accessToken: session.accessToken, refreshToken: session.refreshToken },
    );
  }, [api, session]);

  /**
   * A restored session may have been revoked, or the operator's role removed,
   * while the tab was closed. Confirm it before showing the console rather than
   * letting the first query fail somewhere less obvious.
   */
  useEffect(() => {
    if (!isRestoring) return undefined;

    let cancelled = false;

    void api
      .request({ path: '/admin/auth/me', schema: adminProfileSchema })
      .then((profile) => {
        if (cancelled) return;
        setSession((current) => (current === null ? null : { ...current, admin: profile }));
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        // A revoked session or a removed role ends it. A network blip does not.
        if (cause instanceof ApiError && !cause.isRetryable) applySession(null);
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isRestoring, api, applySession]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await auth.request({
        path: '/admin/auth/login',
        method: 'POST',
        body: { email, password },
        schema: adminSessionSchema,
        skipReauth: true,
      });

      applySession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        admin: result.admin,
      });
    },
    [auth, applySession],
  );

  const refreshToken = session?.refreshToken ?? null;

  const signOut = useCallback(async () => {
    // Cleared first: the operator asked to sign out, and that must happen even
    // if the API is unreachable.
    applySession(null);

    if (refreshToken === null) return;

    await auth
      .request({
        path: '/auth/logout',
        method: 'POST',
        body: { refreshToken },
        schema: z.null(),
        skipReauth: true,
      })
      .catch(() => undefined);
  }, [auth, applySession, refreshToken]);

  const value = useMemo<AuthContextValue>(() => {
    const roles = session?.admin.roles ?? [];

    return {
      status: isRestoring ? 'restoring' : session === null ? 'unauthenticated' : 'authenticated',
      admin: session?.admin ?? null,
      api,
      signIn,
      signOut,
      can: { mutate: canMutate(roles), revealHealth: canRevealHealth(roles) },
    };
  }, [session, isRestoring, api, signIn, signOut]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const value = use(AuthContext);
  if (value === null) {
    throw new Error('useAuth must be used inside an AuthProvider.');
  }
  return value;
}
